import { useContext, useEffect, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { NoticeBox } from "../../ui";
import { ActionDialog, DialogFooter } from "../topActions/shared";
import { approveAllAiImportAPI, fetchAiImportApproveAllAPI } from "../../../utils/api/menuAiImport";
import { aiImportText } from "./aiImportText";
import { menuErrorMessage } from "../menuRole";
import { aiImportErrorMessage } from "./aiImportModel";

// Confirmation of "Approve All". The summary comes from the backend (the same
// rules the approval runs, on the saved draft): what will be approved, which
// edited entries keep their new values, what stays rejected, and what still
// needs the reviewer. Nothing is applied before the user confirms; the
// request carries the revision that was summarized, so a draft changed
// meanwhile is refused instead of approved blindly.
export default function AiImportApproveAllDialog({ authorize, draftId, onClose, onApproved, onBlocked }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const a = t.approveAll;
  const [state, setState] = useState({ loading: true, preview: null, error: "" });
  const [busy, setBusy] = useState(false);
  // One request per confirmation, whatever happens to the button.
  const sent = useRef(false);

  useEffect(() => {
    let alive = true;
    fetchAiImportApproveAllAPI(draftId)
      .then((preview) => alive && setState({ loading: false, preview, error: "" }))
      .catch((err) => alive && setState({ loading: false, preview: null, error: aiImportErrorMessage(err, t, lang) }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const confirm = async () => {
    if (sent.current) return;
    if (!authorize?.()) return setState((prev) => ({ ...prev, error: menuErrorMessage({ status: 403 }, lang) }));
    sent.current = true;
    setBusy(true);
    try {
      onApproved(await approveAllAiImportAPI(draftId, state.preview.revision));
    } catch (err) {
      sent.current = false;
      setBusy(false);
      if (err.code === "APPROVAL_BLOCKED") onBlocked?.(err.details?.problems || []);
      setState((prev) => ({ ...prev, error: aiImportErrorMessage(err, t, lang) }));
    }
  };

  const preview = state.preview;
  const bulk = preview?.bulk;
  const blocking = preview?.plan?.blocking || [];
  // Calories and allergens are approved like every other value the reviewer saw.
  const health = bulk?.healthToConfirm || { calories: 0, allergens: 0 };
  return (
    <ActionDialog
      title={a.title}
      busy={busy}
      error={state.error}
      onClose={onClose}
      footer={<DialogFooter submitLabel={busy ? a.working : a.confirm} tone="success" onSubmit={confirm} onCancel={onClose} busy={busy} disabled={!preview?.approvable} />}
    >
      <p className="menu-note">{a.intro}</p>
      {state.loading && <p className="menu-note">{a.loading}</p>}
      {bulk && (
        <>
          {bulk.entries.approve > 0 ? (
            <ul className="ai-import-approve-all-summary" data-testid="approve-all-summary">
              <li className="is-approve">{a.willApprove(bulk.entries.approve)}</li>
              {bulk.entries.edited > 0 && <li className="is-edited">{a.edited(bulk.entries.edited)}</li>}
              {bulk.entries.alreadyApproved > 0 && <li>{a.already(bulk.entries.alreadyApproved)}</li>}
              {bulk.entries.rejected > 0 && <li className="is-rejected">{a.rejected(bulk.entries.rejected)}</li>}
              {bulk.sections.approve > 0 && <li className="menu-secondary">{a.sections(bulk.sections.approve)}</li>}
              {bulk.pricesToConfirm > 0 && <li className="menu-secondary">{a.prices(bulk.pricesToConfirm)}</li>}
            </ul>
          ) : (
            <NoticeBox variant="info" size="sm">
              {a.nothing}
            </NoticeBox>
          )}
          {blocking.length > 0 && (
            <NoticeBox variant="danger" size="sm" title={a.blockedTitle}>
              <ul className="ai-import-plan-list">
                {blocking.slice(0, 12).map((problem, i) => (
                  <li key={`${problem.code}-${i}`}>{t.issues?.[problem.code] || problem.message}</li>
                ))}
              </ul>
            </NoticeBox>
          )}
          {bulk.entries.approve > 0 && (health.calories > 0 || health.allergens > 0) && <p className="menu-secondary">{a.health(health.calories, health.allergens)}</p>}
        </>
      )}
    </ActionDialog>
  );
}
